import { Component, computed, effect, inject, input, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { finalize } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../error.utils';
import { convertDateToTimezone } from '../../../date.utils';
import { defaultTableDefOptions } from '../../../helpers';
import { AssetRecoveryService } from '../../../service/asset-recovery.service';
import { NotifyService } from '../../../service/notify.service';
import { ReportsService } from '../../../service/reports.service';
import { UserLoginService } from '../../../service/user-login.service';
import { TheftReport } from '../../../types';
import { CellTheftReportActionsComponent } from '../../pages/theft-report/cell-theft-report-actions/cell-theft-report-actions.component';
import { CellTheftReportPhotoComponent } from '../../pages/theft-report/cell-theft-report-photo/cell-theft-report-photo.component';
import { CellTheftReportReviewIdComponent } from '../../pages/theft-report/cell-theft-report-review-id/cell-theft-report-review-id.component';
import { CellTheftReportVideoComponent } from '../../pages/theft-report/cell-theft-report-video/cell-theft-report-video.component';
import { CellTheftReportDatagridInstanceComponent } from './cell-theft-report-datagrid-instance/cell-theft-report-datagrid-instance.component';

export type TheftReportRow = Omit<TheftReport, 'date_shared' | 'date_of_instance'> & {
  date_shared: Date | null;
  date_of_instance: Date;
};

@Component({
  selector: 'app-theft-report-datagrid',
  templateUrl: './theft-report-datagrid.component.html',
  styleUrls: ['./theft-report-datagrid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TheftReportDatagridComponent {
  private userLoginService = inject(UserLoginService);
  private notifyService = inject(NotifyService);
  private assetRecoveryService = inject(AssetRecoveryService);
  private reportsService = inject(ReportsService);
  private router = inject(Router);

  public reportTable = viewChild<AgGridAngular>('reportGrid');
  private gridReady = signal(false);

  public theftReports = input.required<TheftReport[]>();
  public locationName = input.required<string>();
  public locationId = input.required<number>();
  public branchName = input.required<string>();
  public shareKey = input.required<string>();
  public hideBranchCurrency = input.required<boolean>();
  public assetRecoveryEnabled = input.required<boolean>();
  public branchId = input.required<number>();

  public ungroupedTheftReports = computed(() =>
    this.theftReports().map((t) => ({
      ...t,
      date_shared: t.date_shared ? convertDateToTimezone(t.date_shared, t.location_timezone) : null,
      date_of_instance: convertDateToTimezone(new Date(t.date_of_instance), t.location_timezone),
    })),
  );

  public sortChange = output<Sort>();
  public mergeCasesEvent = output<string[]>();
  public markNoTheftEvent = output<string[]>();

  public selectedReports = signal<TheftReportRow[]>([]);
  public selectedCaseIds = computed(() => new Set(this.selectedReports().map((t) => t.case_id)));
  public hasCrossCaseSelection = computed(() => this.selectedCaseIds().size > 1);
  public hasEligibleArSelection = computed(
    () =>
      this.assetRecoveryEnabled() &&
      this.userLoginService.canManageAssetRecovery(String(this.branchId())) &&
      this.selectedReports().length > 0 &&
      !this.selectedReports().some((report) => report.asset_recovery_id),
  );
  public hasAssetRecoveryAccess = computed(
    () => this.assetRecoveryEnabled() && this.userLoginService.canManageAssetRecovery(String(this.branchId())),
  );
  public canCreateArReport = computed(() => this.hasEligibleArSelection() && !this.hasCrossCaseSelection());
  public showCrossCaseWarning = computed(() => this.hasEligibleArSelection() && this.hasCrossCaseSelection());
  public creatingArReport = signal<boolean>(false);
  public creatingReport = signal<boolean>(false);

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    rowHeight: 56,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
    onRowSelected: (event: RowSelectedEvent<TheftReportRow>) => {
      this.selectedReports.update((reports) => {
        if (reports.includes(event.node.data)) return reports.filter((t) => t !== event.node.data);
        return [...reports, event.node.data];
      });
    },
    rowSelection: {
      mode: 'multiRow',
      checkboxLocation: 'autoGroupColumn',
      groupSelects: 'self',
      hideDisabledCheckboxes: true,
    },
    selectionColumnDef: {
      maxWidth: 55,
      suppressHeaderMenuButton: true,
    },
    onGridReady: () => {
      this.gridReady.set(true);
      this.reportTable()?.api.setColumnsVisible(['item_cancellation_value'], !this.hideBranchCurrency());
    },
  };

  public readonly columnDefs: ColDef<TheftReportRow>[] = [
    {
      field: 'review_id',
      headerName: 'Review ID',
      cellDataType: 'text',
      sortable: false,
      minWidth: 320,
      hide: true,
      cellRenderer: CellTheftReportReviewIdComponent,
    },
    {
      field: 'case_num',
      headerName: 'Case #',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: true,
      maxWidth: 100,
    },
    {
      field: 'instance_num',
      headerName: 'Instance #',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: true,
      maxWidth: 140,
      cellRenderer: CellTheftReportDatagridInstanceComponent,
    },
    {
      field: 'date_shared',
      headerName: 'Date Shared',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      sortable: true,
      width: 100,
    },
    {
      field: 'date_of_instance',
      headerName: 'Incident Date',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      sortable: true,
      width: 100,
    },
    {
      field: 'item_cancellation_value',
      headerName: 'Value',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      width: 80,
    },
    {
      headerName: 'Photo',
      sortable: false,
      cellRenderer: CellTheftReportPhotoComponent,
      maxWidth: 80,
    },
    {
      headerName: 'Video',
      sortable: false,
      cellRenderer: CellTheftReportVideoComponent,
      maxWidth: 80,
    },
    {
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      headerName: '',
      suppressKeyboardEvent: () => true,
      sortable: false,
      cellRenderer: CellTheftReportActionsComponent,
      minWidth: 140,
    },
  ];

  constructor() {
    // toggleReviewIdColumn
    effect(() => {
      if (!this.gridReady() || !this.reportTable()?.api) return;
      this.reportTable()!.api.setColumnsVisible(['review_id'], this.userLoginService.currentUserIsAdminOrInternalUser());
    });
  }

  mergeCases() {
    this.mergeCasesEvent.emit(this.selectedReports().map((t) => t.review_id));
    this.selectedReports.set([]);
  }

  markNoTheft() {
    this.markNoTheftEvent.emit(this.selectedReports().map((t) => t.review_id));
    this.selectedReports.set([]);
  }

  reportNameValidate(name: string) {
    const reportName = name.trim();
    if (reportName.length < 4) return 'Report name too short. Min 4 characters.';
    if (reportName.length > 25) return 'Report name too long. Max 25 characters.';

    return null;
  }

  createReport(reportName: string) {
    const title = reportName.trim();
    if (!title) return;

    this.creatingReport.set(true);
    this.reportsService
      .createReport({
        review_outcome_ids: this.selectedReports().map((t) => t.id),
        title,
      })
      .pipe(finalize(() => this.creatingReport.set(false)))
      .subscribe({
        next: (report) => {
          this.notifyService.notify('Report created successfully');
          this.selectedReports.set([]);
          this.router.navigate(['/shared', 'report', report.id]);
        },
        error: (error) => {
          this.notifyService.notify('Failed to create report: ' + tryGetErrorMessage(error));
        },
      });
  }

  createArReport(reportName: string) {
    const title = reportName.trim();
    if (!title) return;

    const locationId = this.locationId();

    this.creatingArReport.set(true);
    this.assetRecoveryService
      .createAssetRecoveryReport(locationId, {
        review_outcome_ids: this.selectedReports().map((t) => t.id),
        title,
      })
      .pipe(finalize(() => this.creatingArReport.set(false)))
      .subscribe({
        next: (report) => {
          this.notifyService.notify('Asset Recovery report created successfully');
          this.selectedReports.set([]);
          this.router.navigate(['/shared', 'asset-recovery-report', report.id]);
        },
        error: (error) => {
          this.notifyService.notify('Failed to create asset recovery report: ' + tryGetErrorMessage(error));
        },
      });
  }

  navigateToAssetRecoveryPage() {
    this.router.navigate(['/home', 'manage', 'branch', this.branchId(), 'location', this.locationId()], {
      queryParams: { subpage: 'asset-recovery' },
    });
  }
}
